import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

/**
 * Minimal accessible combobox (WAI-ARIA 1.2 pattern).
 *
 * Hand-rolled because react-aria/headless-ui would dwarf the bundle for
 * what's effectively a `<select>` with prefix-search. Supports both
 * fully-populated (markets) and search-as-you-type (cities) modes —
 * filtering is delegated to the parent via `onInputChange`, so the
 * caller decides whether to fetch new options as the user types or
 * pre-load everything once.
 */

export interface ComboboxOption {
  /** Stable identifier — what gets passed to onChange and shown in the input. */
  value: string;
  /** Human-readable primary label (defaults to `value` when not provided). */
  label?: string;
  /** Optional dim subtitle (e.g. "12 chains" or "IL_CHI"). */
  hint?: string;
}

export interface ComboboxProps {
  id?: string;
  label: string;
  placeholder?: string;
  /** Selected value, controlled. Empty string means nothing selected. */
  value: string;
  /** Called on every input change (typing). The parent should filter / fetch. */
  onInputChange?: (input: string) => void;
  /** Called when the user picks an option (or clears it). */
  onChange: (value: string) => void;
  /** Options to render in the dropdown. Order matters — already filtered/ranked. */
  options: ComboboxOption[];
  /** Show a small loading hint inside the dropdown. */
  loading?: boolean;
  /** Text rendered when `options` is empty. */
  emptyMessage?: string;
  /** Optional extra content rendered ABOVE the dropdown list. */
  header?: ReactNode;
}

export function Combobox({
  id,
  label,
  placeholder,
  value,
  onInputChange,
  onChange,
  options,
  loading = false,
  emptyMessage = "No matches",
  header,
}: ComboboxProps): JSX.Element {
  const reactId = useId();
  const inputId = id ?? `combobox-${reactId}`;
  const listboxId = `${inputId}-listbox`;

  const [input, setInput] = useState<string>(value);
  const [open, setOpen] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Keep `input` in sync with controlled `value` when it changes externally.
  useEffect(() => {
    setInput(value);
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const handleInput = (next: string) => {
    setInput(next);
    setOpen(true);
    setActiveIndex(-1);
    onInputChange?.(next);
  };

  const select = (opt: ComboboxOption) => {
    setInput(opt.value);
    onChange(opt.value);
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && activeIndex >= 0 && options[activeIndex]) {
        select(options[activeIndex]);
      } else {
        // Plain Enter on free text = commit input as-is (useful for ad-hoc
        // values not in the option list).
        onChange(input.trim());
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        type="text"
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined
        }
        placeholder={placeholder}
        className="input"
        value={input}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />
      {input && (
        <button
          type="button"
          aria-label={`Clear ${label}`}
          onClick={() => {
            setInput("");
            onChange("");
            onInputChange?.("");
          }}
          className="absolute right-2 top-7 text-slate-400 hover:text-slate-700 text-sm"
        >
          ×
        </button>
      )}
      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
        >
          {header}
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-500">Loading…</div>
          )}
          {!loading && options.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-500">
              {emptyMessage}
            </div>
          )}
          {!loading &&
            options.map((opt, i) => {
              const isActive = i === activeIndex;
              return (
                <button
                  key={opt.value}
                  id={`${listboxId}-opt-${i}`}
                  role="option"
                  aria-selected={isActive}
                  type="button"
                  className={[
                    "block w-full text-left px-3 py-1.5 text-sm",
                    isActive
                      ? "bg-brand-50 text-brand-700"
                      : "hover:bg-slate-50 text-slate-900",
                  ].join(" ")}
                  onMouseDown={(e) => {
                    // mousedown not click — keeps focus on the input.
                    e.preventDefault();
                    select(opt);
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <span className="font-medium">{opt.label ?? opt.value}</span>
                  {opt.hint && (
                    <span className="ml-2 text-xs text-slate-400">
                      {opt.hint}
                    </span>
                  )}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
